import type { NavigatorScreenParams } from '@react-navigation/native';
import type { Parameters as MainParameters } from 'app/screens/main-tab';
import type { SpecificNavigationProp } from 'app/types/navigation';

const notificationScreensByTab = {
  billboard: [
    'landing-page',
    'all-listings',
    'neighbour-listings',
    'neighbour-profile',
    'one-listing',
    'listing-activity',
    'ratings',
    'grouped-listing',
    'group-members',
    'one-channel',
    'my-neighbourhood',
    'presentPartageClub',
    'my-information',
    'onboardingIntro',
  ],
  chat: [
    'all-channels',
    'one-channel',
    'loan-dates-picker',
    'listing-bonification',
    'new-conversation',
    'new-group-chat',
    'chat-members',
    'add-chat-members',
    'chat-how-it-works',
  ],
  management: [
    'management-overview',
    'my-address',
    'my-loans',
    'my-listings',
    'neighbour-profile',
    'neighbour-listings',
    'one-channel',
    'one-listing',
    'listing-activity',
    'ratings',
    'one-loan',
    'loan-participants',
    'past-loans',
    'favourites',
    'group-members',
    'loan-dates-picker',
  ],
  notifications: ['all-notifications', 'group-members'],
  profile: [
    'account-deletion',
    'calendar-setting',
    'contact-us',
    'my-address',
    'change-password',
    'my-information',
    'my-plan',
    'my-preferences',
    'my-ratings',
    'streak',
    'super-partageur',
    'super-partageur-details',
    'ambassador',
    'my-impact-financial',
    'notifications-chat-setting',
    'notifications-need-setting',
    'notifications-setting',
    'profile-overview',
    'language',
    'payment',
    'privacy',
    'group-members',
    'my-clubs',
    'my-interests',
    'my-neighbourhood',
    'unavailable',
  ],
} as const;

type NotificationTab = keyof typeof notificationScreensByTab;

function isNotificationTab(tab: string): tab is NotificationTab {
  return tab in notificationScreensByTab;
}

function isAllowedNotificationScreen(tab: NotificationTab, screen: string) {
  return (notificationScreensByTab[tab] as readonly string[]).includes(screen);
}

export function navigateToNotificationDestination(
  navigation: Pick<SpecificNavigationProp, 'navigate'>,
  tab: string,
  screen: string,
  params: Record<string, string | string[]>,
) {
  // The URL uses /overview; the profile navigator registers profile-overview.
  if (tab === 'profile' && screen === 'overview') screen = 'profile-overview';
  if (!isNotificationTab(tab) || !isAllowedNotificationScreen(tab, screen)) return;

  // Notifications can live in a root overlay, outside the main tab navigator.
  // Return to the existing main route and remove overlays above it in one action.
  navigation.navigate(
    'main',
    {
      screen: tab,
      params: { screen, params, initial: false },
    } as NavigatorScreenParams<MainParameters>,
    { pop: true },
  );
}
